import type { BalanceThemeRaw } from '@balance-web/theme';

import type { AvatarSizeType, ShapeType, SizeType } from './types';
import { isCustomAvatarSize } from './utils';

/** What the styles actually need, whether it came from a token or not. */
type ResolvedSize = {
  width: number | string;
  height: number | string;
  fontSize: number | string;
  borderRadius: number | string;
};

type Size = {
  fontSizeDisplay: string;
  fontSizeText: string;
  borderRadius: number;
  boxSize: number;
  gap: number;
  paddingX: number;
  paddingY: number;
  stackMask: number;
};

export type avatarStylesProps = {
  background: string;
  foreground: string;
  border?: string;
  shape: ShapeType;
  size: ResolvedSize;
};

export const getBaseStyles = ({
  background,
  foreground,
  border,
  shape,
  size,
}: avatarStylesProps) => {
  const borderRadius = shape === 'round' ? '50%' : size.borderRadius;

  const styles = {
    alignItems: 'center',
    backgroundColor: background,
    boxShadow: border ? `0 0 0 1px ${border}` : undefined,
    borderRadius: borderRadius,
    color: foreground,
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: size.fontSize,
    fontWeight: 500,
    height: size.height,
    justifyContent: 'center',
    lineHeight: 1,
    textAlign: 'center',
    width: size.width,
  } as const;

  return styles;
};

/**
 * Flattens either form of the `size` prop into the four values the styles use.
 * A custom size carries no radius of its own, so square avatars fall back to
 * the `small` token's — the scale's most common corner.
 */
export const resolveAvatarSize = (
  sizes: ReturnType<typeof getSizes>,
  size: AvatarSizeType
): ResolvedSize => {
  if (isCustomAvatarSize(size)) {
    return {
      width: size.width,
      height: size.height,
      fontSize: size.fontSize,
      borderRadius: size.borderRadius ?? sizes.small.borderRadius,
    };
  }

  const token = sizes[size];

  return {
    width: token.boxSize,
    height: token.boxSize,
    fontSize: token.fontSizeDisplay,
    borderRadius: token.borderRadius,
  };
};

export const getSizes = (theme: BalanceThemeRaw) => {
  const sizes: Record<SizeType, Size> = {
    xsmall: {
      fontSizeDisplay: theme.typography.fontSize.small, // larger because only a single character is displayed at this size
      fontSizeText: theme.typography.fontSize.xsmall,
      borderRadius: theme.radii.xsmall,
      boxSize: theme.sizing.xsmall, // 24
      gap: theme.spacing.xsmall,
      paddingX: theme.spacing.small,
      paddingY: theme.spacing.xsmall,
      stackMask: 1,
    },
    small: {
      fontSizeDisplay: theme.typography.fontSize.xsmall,
      fontSizeText: theme.typography.fontSize.small,
      borderRadius: theme.radii.small,
      boxSize: theme.sizing.small, // 32
      gap: theme.spacing.small,
      paddingX: theme.spacing.medium,
      paddingY: theme.spacing.xsmall,
      stackMask: 2,
    },
    medium: {
      fontSizeDisplay: theme.typography.fontSize.large,
      fontSizeText: theme.typography.fontSize.medium,
      borderRadius: theme.radii.medium,
      boxSize: theme.sizing.medium, // 48
      gap: theme.spacing.small,
      paddingX: theme.spacing.medium,
      paddingY: theme.spacing.small,
      stackMask: 3,
    },
    large: {
      fontSizeDisplay: theme.typography.fontSize.xlarge,
      fontSizeText: theme.typography.fontSize.large,
      borderRadius: theme.radii.medium,
      boxSize: theme.sizing.large, // 56
      gap: theme.spacing.medium,
      paddingX: theme.spacing.xlarge,
      paddingY: theme.spacing.medium,
      stackMask: 4,
    },
    xlarge: {
      fontSizeDisplay: theme.typography.fontSize.xxlarge,
      fontSizeText: theme.typography.fontSize.xlarge,
      borderRadius: theme.radii.large,
      boxSize: theme.sizing.xlarge, // 72
      gap: theme.spacing.large,
      paddingX: theme.spacing.xxlarge,
      paddingY: theme.spacing.large,
      stackMask: 5,
    },
  } as const;
  return sizes;
};
